import { useState, useEffect } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { useParams } from 'react-router-dom';
import { 
  generateHreflangLinks, 
  getCanonicalUrl, 
  generatePublicationHreflangLinks,
  generateTopicHreflangLinks,
  generateTopicsListingHreflangLinks
} from '@/lib/hreflangUtils';

interface UseSEOOptions {
  routeKey?: string;
  title: string;
  description: string;
  type?: 'website' | 'article' | 'profile';
  image?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string[];
  schema?: object;
  // Publication-specific options
  publicationId?: string;
  vanityUrl?: string;
  // Topic-specific options
  topicId?: string;
  topicSlug?: string;
  locationSlug?: string;
  isTopicsListing?: boolean;
}

interface SEOData {
  title: string;
  description: string;
  type: 'website' | 'article' | 'profile';
  image?: string;
  url: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string[];
  schema?: object;
  canonicalUrl: string;
  hreflangLinks: Array<{ lang: string; url: string }>;
  locale: string;
  isLoading: boolean;
}

/**
 * Hook to generate comprehensive SEO metadata with localization support
 * Automatically generates hreflang links and canonical URLs based on current route
 * 
 * @param options - SEO configuration options
 * @returns Complete SEO data ready for SEOHead component
 */
export const useSEO = (options: UseSEOOptions): SEOData => {
  const { settings } = useLocale();
  const params = useParams<{ nativeSlug?: string }>();
  const [hreflangLinks, setHreflangLinks] = useState<Array<{ lang: string; url: string }>>([]);
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : baseUrl;

  useEffect(() => {
    const fetchSEOData = async () => {
      setIsLoading(true);
      
      try {
        const nativeSlug = params.nativeSlug;
        const lang = settings.language.toLowerCase();

        // Check if this is a topic page
        if (options.topicId && options.topicSlug) {
          const topicHreflang = await generateTopicHreflangLinks(
            options.topicId,
            options.topicSlug,
            options.locationSlug,
            baseUrl
          );
          
          setHreflangLinks(topicHreflang);
          setCanonicalUrl(currentUrl);
        }
        // Check if this is a topics listing page
        else if (options.isTopicsListing) {
          const listingHreflang = await generateTopicsListingHreflangLinks(
            options.locationSlug,
            baseUrl
          );
          
          setHreflangLinks(listingHreflang);
          setCanonicalUrl(currentUrl);
        }
        // Check if this is a publication
        else if (options.publicationId && options.vanityUrl) {
          const publicationHreflang = await generatePublicationHreflangLinks(
            options.publicationId,
            options.vanityUrl,
            baseUrl
          );

          setHreflangLinks(publicationHreflang);
          setCanonicalUrl(currentUrl);
        } 
        // Route-based pages
        else if (options.routeKey) {
          const [hreflang, canonical] = await Promise.all([
            generateHreflangLinks(options.routeKey, nativeSlug, baseUrl),
            getCanonicalUrl(options.routeKey, lang, nativeSlug, baseUrl)
          ]);

          setHreflangLinks(hreflang);
          // STRICT: Only set canonical if translation exists, otherwise use current URL
          setCanonicalUrl(canonical ?? currentUrl);
        } else {
          setCanonicalUrl(currentUrl);
          setHreflangLinks([]);
        }
      } catch {
        setCanonicalUrl(currentUrl);
        setHreflangLinks([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSEOData();
  }, [options.routeKey, options.publicationId, options.vanityUrl, options.topicId, options.topicSlug, options.locationSlug, options.isTopicsListing, settings.language, params.nativeSlug, baseUrl, currentUrl]);

  return {
    title: options.title,
    description: options.description,
    type: options.type || 'website',
    image: options.image,
    url: currentUrl,
    author: options.author,
    publishedTime: options.publishedTime,
    modifiedTime: options.modifiedTime,
    keywords: options.keywords,
    schema: options.schema,
    canonicalUrl,
    hreflangLinks,
    locale: (settings.locale || settings.language).replace('-', '_'),
    isLoading,
  };
};
